import { useEffect, useRef, useState } from 'react';
import { Navigate, ToolbarProps, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import getISOWeek from 'date-fns/getISOWeek';
import enUS from 'date-fns/locale/en-US';
import nb from 'date-fns/locale/nb';
import { useTranslation } from 'react-i18next';

import { CaretIcon, CheckMark } from 'src/components/Icons/icons';
import { CalendarNavigation } from './CalendarNavigation';
import {
    CalendarMode,
    CalendarSlotEvent,
    calendarViews,
    formatWeekMonthLabel,
    MyEventsFilter,
} from './calendarModel';

export const CalendarToolbar = ({
    date,
    view,
    views,
    label,
    localizer: { messages },
    onNavigate,
    onView,
    calendarMode,
    myEventsFilter,
    onMyEventsFilterChange,
    showMyEventsFilter,
}: ToolbarProps<CalendarSlotEvent> & {
    calendarMode: CalendarMode;
    myEventsFilter: MyEventsFilter;
    onMyEventsFilterChange: (filter: MyEventsFilter) => void;
    showMyEventsFilter: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);
    const [isMyEventsFilterMenuOpen, setIsMyEventsFilterMenuOpen] =
        useState(false);
    const viewPickerRef = useRef<HTMLDivElement>(null);
    const viewPickerButtonRef = useRef<HTMLButtonElement>(null);
    const myEventsFilterPickerRef = useRef<HTMLDivElement>(null);
    const myEventsFilterButtonRef = useRef<HTMLButtonElement>(null);
    const availableViews = Array.isArray(views)
        ? views
        : calendarViews.filter(availableView => views[availableView]);
    const showWeekNumber = view === Views.WEEK || view === Views.DAY;
    const calendarLocale = i18n.language.startsWith('en') ? enUS : nb;
    const displayLabel =
        view === Views.AGENDA
            ? format(date, 'LLLL yyyy', { locale: calendarLocale })
            : view === Views.WEEK
            ? formatWeekMonthLabel(date, calendarLocale)
            : label;

    useEffect(() => {
        if (!isViewMenuOpen && !isMyEventsFilterMenuOpen) return;

        const closeOnOutsideClick = (event: MouseEvent) => {
            if (
                isViewMenuOpen &&
                viewPickerRef.current &&
                !viewPickerRef.current.contains(event.target as Node)
            ) {
                setIsViewMenuOpen(false);
            }
            if (
                isMyEventsFilterMenuOpen &&
                myEventsFilterPickerRef.current &&
                !myEventsFilterPickerRef.current.contains(event.target as Node)
            ) {
                setIsMyEventsFilterMenuOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;

            if (isViewMenuOpen) {
                setIsViewMenuOpen(false);
                viewPickerButtonRef.current?.focus();
            }
            if (isMyEventsFilterMenuOpen) {
                setIsMyEventsFilterMenuOpen(false);
                myEventsFilterButtonRef.current?.focus();
            }
        };

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [isMyEventsFilterMenuOpen, isViewMenuOpen]);

    return (
        <div className="rbc-toolbar">
            <CalendarNavigation
                onToday={() => onNavigate(Navigate.TODAY)}
                onPrevious={() => onNavigate(Navigate.PREVIOUS)}
                onNext={() => onNavigate(Navigate.NEXT)}
            />
            <span className="rbc-toolbar-label">
                <span>{displayLabel}</span>
                {showWeekNumber ? (
                    <span className="mundu-calendar-week-number">
                        {t('modules.calendar.views.weekNumber', {
                            week: getISOWeek(date),
                        })}
                    </span>
                ) : null}
            </span>
            {calendarMode === 'calendar' ? (
                <div
                    ref={viewPickerRef}
                    className="mundu-calendar-view-picker mundu-calendar-toolbar-actions"
                >
                    <button
                        ref={viewPickerButtonRef}
                        type="button"
                        className="mundu-calendar-view-picker-trigger"
                        aria-label={t(
                            'modules.calendar.views.viewPickerAriaLabel',
                        )}
                        aria-haspopup="menu"
                        aria-expanded={isViewMenuOpen}
                        onClick={() => setIsViewMenuOpen(isOpen => !isOpen)}
                    >
                        <span>{messages[view]}</span>
                        <span
                            className="relative block h-3 w-3 flex-shrink-0"
                            aria-hidden="true"
                        >
                            <CaretIcon />
                        </span>
                    </button>
                    {isViewMenuOpen ? (
                        <div
                            className="mundu-calendar-view-menu"
                            role="menu"
                            aria-label={t(
                                'modules.calendar.views.viewPickerAriaLabel',
                            )}
                        >
                            {availableViews.map(availableView => {
                                const isSelected = view === availableView;

                                return (
                                    <button
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={isSelected}
                                        key={availableView}
                                        className={
                                            isSelected
                                                ? 'mundu-calendar-view-option-active'
                                                : undefined
                                        }
                                        onClick={() => {
                                            onView(availableView);
                                            setIsViewMenuOpen(false);
                                        }}
                                    >
                                        <span>{messages[availableView]}</span>
                                        <span
                                            className="mundu-calendar-view-option-check"
                                            aria-hidden="true"
                                        >
                                            {isSelected ? <CheckMark /> : null}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            ) : showMyEventsFilter ? (
                <>
                    <span
                        className="rbc-btn-group mundu-calendar-toolbar-actions mundu-calendar-my-events-filter-buttons"
                        role="radiogroup"
                        aria-label={t(
                            'modules.calendar.myEvents.filterAriaLabel',
                        )}
                    >
                        <button
                            type="button"
                            role="radio"
                            aria-checked={myEventsFilter === 'department'}
                            className={
                                myEventsFilter === 'department'
                                    ? 'rbc-active'
                                    : undefined
                            }
                            onClick={() => onMyEventsFilterChange('department')}
                        >
                            {t('modules.calendar.myEvents.allInDepartment')}
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={myEventsFilter === 'createdByMe'}
                            className={
                                myEventsFilter === 'createdByMe'
                                    ? 'rbc-active'
                                    : undefined
                            }
                            onClick={() =>
                                onMyEventsFilterChange('createdByMe')
                            }
                        >
                            {t('modules.calendar.myEvents.createdByMe')}
                        </button>
                    </span>
                    <div
                        ref={myEventsFilterPickerRef}
                        className="mundu-calendar-view-picker mundu-calendar-toolbar-actions mundu-calendar-my-events-filter-picker"
                    >
                        <button
                            ref={myEventsFilterButtonRef}
                            type="button"
                            className="mundu-calendar-view-picker-trigger"
                            aria-label={t(
                                'modules.calendar.myEvents.filterAriaLabel',
                            )}
                            aria-haspopup="menu"
                            aria-expanded={isMyEventsFilterMenuOpen}
                            onClick={() =>
                                setIsMyEventsFilterMenuOpen(isOpen => !isOpen)
                            }
                        >
                            <span>
                                {t(
                                    myEventsFilter === 'department'
                                        ? 'modules.calendar.myEvents.departmentShort'
                                        : 'modules.calendar.myEvents.createdByMeShort',
                                )}
                            </span>
                            <span
                                className="relative block h-3 w-3 flex-shrink-0"
                                aria-hidden="true"
                            >
                                <CaretIcon />
                            </span>
                        </button>
                        {isMyEventsFilterMenuOpen ? (
                            <div
                                className="mundu-calendar-view-menu"
                                role="menu"
                                aria-label={t(
                                    'modules.calendar.myEvents.filterAriaLabel',
                                )}
                            >
                                {(
                                    [
                                        'department',
                                        'createdByMe',
                                    ] as MyEventsFilter[]
                                ).map(filter => {
                                    const isSelected =
                                        myEventsFilter === filter;

                                    return (
                                        <button
                                            type="button"
                                            role="menuitemradio"
                                            aria-checked={isSelected}
                                            key={filter}
                                            className={
                                                isSelected
                                                    ? 'mundu-calendar-view-option-active'
                                                    : undefined
                                            }
                                            onClick={() => {
                                                onMyEventsFilterChange(filter);
                                                setIsMyEventsFilterMenuOpen(
                                                    false,
                                                );
                                            }}
                                        >
                                            <span>
                                                {t(
                                                    filter === 'department'
                                                        ? 'modules.calendar.myEvents.allInDepartment'
                                                        : 'modules.calendar.myEvents.createdByMe',
                                                )}
                                            </span>
                                            <span
                                                className="mundu-calendar-view-option-check"
                                                aria-hidden="true"
                                            >
                                                {isSelected ? (
                                                    <CheckMark />
                                                ) : null}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                </>
            ) : null}
        </div>
    );
};
